import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { gr } from "../tokens";

// Small trend line beside a number (e.g. a result's last five check-ins): brand line, dot on the latest value.
// points = [{ value: number, label?: string }], oldest first; 2+ points draw a line.
// Hovering a point shows its label (native tooltip); `ariaLabel` describes the whole trend for screen readers.
// Shrinks with its box when space is short (never wider than its container).
const PAD = 4; // room for the end dot (r 3) and the 2px line

export default function GrSparkline({ points, width = 112, height = 28, ariaLabel, sx }) {
  const values = (points || []).filter((p) => Number.isFinite(p?.value));
  if (values.length < 2) return null;
  const nums = values.map((p) => p.value);
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const x = (i) => PAD + (i * (width - PAD * 2)) / (values.length - 1);
  // A flat trend sits in the middle.
  const y = (v) =>
    max === min ? height / 2 : PAD + ((max - v) * (height - PAD * 2)) / (max - min);
  const coords = values.map((p, i) => [x(i), y(p.value)]);
  const last = coords[coords.length - 1];
  return (
    <Box
      component="svg"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel}
      sx={{
        flex: "none",
        display: "block",
        maxWidth: "100%",
        height: "auto",
        overflow: "visible",
        ...sx,
      }}
    >
      <polyline
        points={coords.map(([px, py]) => `${px},${py}`).join(" ")}
        fill="none"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ stroke: gr.color.brand }}
      />
      <circle cx={last[0]} cy={last[1]} r="3" style={{ fill: gr.color.brand }} />
      {/* Hover targets, larger than the line. */}
      {coords.map(([px, py], i) => (
        <circle key={i} cx={px} cy={py} r="8" fill="transparent">
          {values[i].label ? <title>{values[i].label}</title> : null}
        </circle>
      ))}
    </Box>
  );
}

GrSparkline.propTypes = {
  points: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.number, label: PropTypes.string })
  ).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  ariaLabel: PropTypes.string,
  sx: PropTypes.object,
};
